import {Suspense, useContext} from 'react'
import _ from 'lodash'
import {BrowserRouter, Location, useLocation} from 'react-router-dom'
import {Container, Dimmer, Loader} from 'semantic-ui-react'
import {RestfulProvider} from 'restful-react'
import {Helmet} from 'react-helmet'
import {MainMenu} from '@/components/main-menu'
import {Cheats} from '@/features/cheats'
import {UploadContextWrapper} from '@/features/uploads'
import {AuthContext, AuthWall} from '@/features/auth'
import {
  ErrorBoundary,
  MessageContext,
  NotificationWidget,
  NotificationsWrap,
} from '@/features/errors'
import {LabelingContextWrapper} from './features/labeling'
import {AppRoutes} from './routes'
import {DataSourcesContextWrapper} from './features/data-sources'
import {NavStateContextWrapper} from './features/nav-state/context'
import {ClientAnalyticsProvider, ClientAnalyticsPageView} from './features/analytics'
import {DarkModeContextWrapper} from './features/dark-mode'

function isEmbeddedLocation(location: Location) {
  return _.chain(location).get('pathname').toLower().startsWith('/embed/').value()
}

function App() {
  const location = useLocation()
  const classNames = _.chain(['App', isEmbeddedLocation(location) ? 'embed' : undefined])
    .compact()
    .join(' ')
    .value()
  return (
    <ClientAnalyticsProvider>
      <DataSourcesContextWrapper>
        <LabelingContextWrapper>
          <div className={classNames}>
            <Container fluid>
              <NavStateContextWrapper>
                <UploadContextWrapper>
                  {isEmbeddedLocation(location) ? null : <MainMenu />}
                  <div className='main-content'>
                    <Cheats>
                      <ErrorBoundary>
                        <Suspense
                          fallback={
                            <Dimmer active>
                              <Loader />
                            </Dimmer>
                          }
                        >
                          <AppRoutes />
                          <ClientAnalyticsPageView />
                        </Suspense>
                      </ErrorBoundary>
                    </Cheats>
                  </div>
                </UploadContextWrapper>
              </NavStateContextWrapper>
            </Container>
          </div>
        </LabelingContextWrapper>
      </DataSourcesContextWrapper>
    </ClientAnalyticsProvider>
  )
}

function AuthenticatedApp() {
  const {signOut} = useContext(AuthContext)

  const {logError} = useContext(MessageContext)

  return (
    // @ts-expect-error TODO: replace restful-react since it's deprecated
    <RestfulProvider
      base='/'
      onError={async ({status, message, data}) => {
        if (status === 401) {
          await signOut()
          window.location.reload()
        }
        const msg = _.chain([
          message,
          _.get(data, 'detail'),
          _.chain(_.get(data, 'errors', {}))
            .toPairs()
            .map(([k, v]) => `- ${k}: ${v}`)
            .value(),
        ])
          .flatten()
          .compact()
          .filter((val) => _.isString(val))
          .join('\n')
          .value()
        logError(msg, data)
      }}
    >
      <BrowserRouter>
        <DarkModeContextWrapper>
          <AuthWall>
            <App />
          </AuthWall>
        </DarkModeContextWrapper>
      </BrowserRouter>
    </RestfulProvider>
  )
}

function NotificationsApp() {
  return (
    <NotificationsWrap>
      <Helmet>
        <title>Vionlabs</title>
      </Helmet>
      <AuthenticatedApp />
      <NotificationWidget />
    </NotificationsWrap>
  )
}

export default NotificationsApp
